import { useRsrc } from '@/common/rsrc/RsrcContext';
import {
  Checkbox,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';

/**
 *
 * @param {*} onSelectionChange Handle
 * @param {*} selectedStateCodes test
 * @param {*} initOpenGroups
 * @returns
 */
export const McdAnalystStateSelector = ({ onSelectionChange, onGroupsChange, selectedStateCodes, initOpenGroups }) => {
  const rsrc = useRsrc();

  const [analystStates, setAnalystStates] = useState({});

  const [selectedStates, setSelectedStates] = useState([]);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    rsrc.data('sc', 'MCD_ANALYST_STATE_DATA_PLUGIN', {}).then((data) => {
      if (selectedStateCodes) {
        let selectedStates = Object.values(data)
          .flat()
          .filter((state) => {
            return selectedStateCodes.includes(state.code);
          });
        setSelectedStates(selectedStates);
      }
      if (initOpenGroups) {
        setOpenGroups(initOpenGroups);
      } else {
        const initialOpenGroups = Object.keys(data).reduce((accumulator, analyst) => {
          accumulator[analyst] = true;
          return accumulator;
        }, {});
        setOpenGroups(initialOpenGroups);
      }
      setAnalystStates(data);
    });
  }, []);

  useEffect(() => {
    onSelectionChange(selectedStates);
  }, [selectedStates, onSelectionChange]);

  useEffect(() => {
    if (onGroupsChange) {
      onGroupsChange(openGroups);
    }
  }, [openGroups]);

  const handleStateToggle = (state) => {
    const isSelected = selectedStates.some((s) => s.code === state.code);
    if (isSelected) {
      setSelectedStates(selectedStates.filter((s) => s.code !== state.code));
    } else {
      setSelectedStates([...selectedStates, state]);
    }
  };

  return (
    <TextField
      fullWidth
      select
      variant="outlined"
      label="States"
      value={selectedStates}
      SelectProps={{
        multiple: true,
        displayEmpty: true,
        renderValue: (selected) => (selected.length === 0 ? 'All' : selected.map((p) => p.code).join(', ')),
      }}
    >
      {Object.entries(analystStates).map(([analyst, states]) => {
        const allSelected = states.every((state) => selectedStates.some((sel) => sel.code === state.code));
        const partiallySelected =
          states.some((state) => selectedStates.some((sel) => sel.code === state.code)) && !allSelected;

        return (
          <div key={analyst}>
            <MenuItem>
              <Checkbox
                checked={allSelected}
                indeterminate={partiallySelected}
                onClick={(e) => {
                  e.stopPropagation();
                  const statesForAnalyst = analystStates[analyst] || [];
                  const allSelected = statesForAnalyst.every((state) =>
                    selectedStates.some((sel) => sel.code === state.code),
                  );

                  if (allSelected) {
                    setSelectedStates(
                      selectedStates.filter((s) => !statesForAnalyst.some((state) => state.code === s.code)),
                    );
                  } else {
                    const newStates = statesForAnalyst.filter(
                      (state) => !selectedStates.some((sel) => sel.code === state.code),
                    );
                    setSelectedStates([...selectedStates, ...newStates]);
                  }
                }}
              />
              <ListItemText primary={analyst} />
              <span
                style={{ marginLeft: 'auto', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenGroups((prev) => ({
                    ...prev,
                    [analyst]: !prev[analyst],
                  }));
                }}
              >
                {openGroups[analyst] ? '▼' : '▶'}
              </span>
            </MenuItem>

            <Collapse in={openGroups[analyst]} timeout="auto" unmountOnExit>
              <List component="div" dense disablePadding sx={{ py: 0 }}>
                {states.map((state) => (
                  <ListItem key={state.code} disablePadding sx={{ pl: 4 }}>
                    <ListItemButton sx={{ py: 0.25, minHeight: 32 }} onClick={() => handleStateToggle(state)}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox size="small" checked={selectedStates.some((s) => s.code === state.code)} />
                      </ListItemIcon>
                      <ListItemText primary={state.code} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </div>
        );
      })}
    </TextField>
  );
};
